import './schedule.scss';
import { Button } from '../index';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import React, { ReactElement, useEffect, useState } from 'react';
import moment, { Moment } from 'moment-timezone';

// TODO: handle event isAvailable
// TODO: handle multiple events in a single slot
// TODO: handle timezones correctly
// TODO: removing selections
// TODO: handle events at/across day boundaries
// TODO: recommended time slots
// TODO: date/time labels
// TODO: Handle non-aligned events
// TODO: Handle all day events

const formatTime = (start: Moment, end: Moment) => {
    const startString = start.format('HH:mm');
    const endString = end.format('HH:mm');

    return `${startString} - ${endString}`;
};

const getSlots = (event: ScheduleEvent) => {
    const start = moment(event.startTime);
    const duration = event.duration();

    if (!(start.minute() % 15 === 0)) {
        throw new Error('Invalid Start Time');
    }

    const offset = start.minute() / 15;
    const startSlot = start.hour() * 4 + offset;
    const endSlot = startSlot + duration / 15;

    return {
        startSlot,
        endSlot,
    };
};

/*
 * Event is the core data model for the Schedule component. It is a subset
 * of the Event model in EtchSchedule
 */
export interface Event {
    title?: string;
    startTime: string;
    endTime: string;
    isAllDay?: boolean;
    isAvailable?: boolean;
}

/*
 * ScheduleEvent is the view model for the Schedule component. It is a superset
 * of Event that also includes UI specific fields and helper methods.
 */
export class ScheduleEvent implements Event {
    className?: string;

    title?: string;
    startTime: string;
    endTime: string;
    isAllDay?: boolean;
    isAvailable?: boolean;

    constructor(event: Event, className?: string) {
        this.className = className;

        this.title = event.title;
        this.startTime = event.startTime;
        this.endTime = event.endTime;
        this.isAllDay = event.isAllDay;
        this.isAvailable = event.isAvailable;
    }

    duration = () => {
        const start = moment(this.startTime);
        const end = moment(this.endTime);
        return moment.duration(end.diff(start)).asMinutes();
    };
}

class Day {
    date: Moment;
    events: ScheduleEvent[];
    selections: ScheduleEvent[];
    unavailable: ScheduleEvent[];
    selectDuration: number;

    /*
     * `slots` and `busy` are arrays where each index represents a 15 minute
     * slot. These are used for quickly indexing against the UI which is
     * divided in the same manner.
     *
     * `slots` only contains events that begin during that time slice, but
     * `busy` contains a value for every slot an event occupies. For example,
     * if you had a one hour event at 8:00, slots[32] would contain that event,
     * and busy[32] through busy[35] would be set to `true`.
     */
    slots: ScheduleEvent[];
    busy: boolean[];

    constructor(
        date: Moment,
        events: ScheduleEvent[],
        unavailable: ScheduleEvent[],
        selectDuration: number,
        selections?: ScheduleEvent[]
    ) {
        this.date = date;
        this.selectDuration = selectDuration;
        // Filter events that aren't on this day
        const dayFilterFunc = (event: ScheduleEvent) => {
            return moment(event.startTime) >= date && moment(event.startTime) <= date.clone().endOf('day');
        };
        this.events = events.filter(dayFilterFunc);
        this.unavailable = unavailable.filter(dayFilterFunc);
        this.selections = selections || [];
        this.slots = [];
        this.busy = [];

        for (const event of [...this.events, ...this.selections]) {
            this.addEvent(event);
            this.markBusy(event);
        }

        for (const event of this.unavailable) {
            this.markBusy(event);
        }
        this.markBusySlots();
    }

    addEvent = (event: ScheduleEvent) => {
        const slots = getSlots(event);
        this.slots[slots.startSlot] = event;
    };

    markBusy = (event: ScheduleEvent) => {
        const slots = getSlots(event);

        for (let slot = slots.startSlot; slot < slots.endSlot; slot++) {
            this.busy[slot] = true;
        }
    };

    markBusySlots = () => {
        this.busy = markBusySlots(this.busy, this.selectDuration);
    };

    createSelection = (hour: number, minute: number, duration: number) => {
        const start = this.date.clone().set({
            hour: hour,
            minute: minute,
            second: 0,
        });
        const end = start.clone().add(duration * 60 * 1000);

        return new ScheduleEvent(
            {
                title: formatTime(start, end),
                startTime: start.format(),
                endTime: end.format(),
            },
            'rain-schedule-selection'
        );
    };

    withSelection = (selection: ScheduleEvent) => {
        return new Day(this.date, this.events, this.unavailable, this.selectDuration, [...this.selections, selection]);
    };

    withoutSelection = (index: number) => {
        const selection = this.slots[index];
        const selections = this.selections.filter((event) => event !== selection);
        return new Day(this.date, this.events, this.unavailable, this.selectDuration, selections);
    };

    isSelection = (event: ScheduleEvent) => {
        return this.selections.includes(event);
    };

    getEvent = (index: number) => {
        return this.slots[index];
    };

    getBusy = (index: number) => {
        return Boolean(this.busy[index]);
    };

    isBusy = (event: ScheduleEvent) => {
        const slots = getSlots(event);

        for (let slot = slots.startSlot; slot < slots.endSlot; slot++) {
            if (this.getBusy(slot)) {
                return true;
            }
        }

        return false;
    };

    getSelections = () => {
        return this.selections;
    };
}

interface ScheduleProps {
    startDate: string;
    endDate: string;
    primaryTimeZone: string;
    secondaryTimeZone?: string;
    events: ScheduleEvent[];
    unavailable: ScheduleEvent[];
    onNextClick?: () => void;
    onPrevClick?: () => void;
    selectDuration: number;
    onSelectionChange: (dates: Event[]) => void;
}

const ALPHA_Schedule: React.FC<ScheduleProps> = (props: ScheduleProps) => {
    const getInitialState = () => {
        if (props.selectDuration && props.selectDuration % 15 !== 0) {
            throw new Error('Invalid Duration');
        }

        const currentDate = moment.tz(props.startDate, props.primaryTimeZone);
        const endDate = moment.tz(props.endDate, props.primaryTimeZone);
        const days = [];

        while (endDate.diff(currentDate) >= 0) {
            days.push(new Day(currentDate.clone(), props.events, props.unavailable, props.selectDuration));
            currentDate.add(24 * 60 * 60 * 1000);
        }

        return days;
    };

    const [days, setDays] = useState<Day[]>(getInitialState());

    useEffect(() => {
        props.onSelectionChange(getSelections());
    }, [days]);

    const onSlotSelect = (dayIndex: number, index: number) => {
        if (!props.selectDuration) {
            return;
        }

        const daysSelect = [...days];
        const hour = Math.floor(index / 4);
        const minute = (index % 4) * 15;

        const daySelect = daysSelect[dayIndex];

        if (daySelect) {
            const selection = daySelect.createSelection(hour, minute, props.selectDuration);
            if (!daySelect.isBusy(selection)) {
                daysSelect[dayIndex] = daySelect.withSelection(selection);
                setDays(daysSelect);
            }
        }
    };

    const onSelectionClick = (dayIndex: number, selectionIndex: number) => {
        const daysSelect = [...days];

        const daySelect = daysSelect[dayIndex];

        if (daySelect) {
            daysSelect[dayIndex] = daySelect.withoutSelection(selectionIndex);
        }

        setDays(daysSelect);
    };

    const getSelections = () => {
        let selections: ScheduleEvent[] = [];

        for (const day of days) {
            selections = selections.concat(day.getSelections());
        }

        return selections;
    };

    const { primaryTimeZone, secondaryTimeZone, onPrevClick, onNextClick } = props;

    const dayElements = days.map((day, dayIndex) => {
        const hourElements: ReactElement[] = [];

        // 24 hours in a day * 4 slots per hour
        for (let hour = 0; hour < 24; hour++) {
            const slotElements = [];

            for (let slot = 0; slot < 4; slot++) {
                const index = hour * 4 + slot;
                const event = day.getEvent(index);
                const className = day.getBusy(index) ? 'rain-schedule-slot busy' : 'rain-schedule-slot';
                slotElements.push(
                    <div key={`${2 * index}s`} className={className} onClick={() => onSlotSelect(dayIndex, index)}>
                        {event ? (
                            <div
                                className={`rain-schedule-event ${event.className}`}
                                style={{ height: (15 * event.duration()) / 15 }}
                                onClick={
                                    day.isSelection(event)
                                        ? () => {
                                              onSelectionClick(dayIndex, index);
                                          }
                                        : undefined
                                }>
                                {event.title}
                            </div>
                        ) : undefined}
                    </div>
                );
            }

            hourElements.push(
                <div key={hour * 2} className="rain-schedule-hour">
                    {slotElements}
                </div>
            );
        }

        return hourElements;
    });

    const times: Moment[] = [];
    const day = days[0] ?? { date: moment() };
    const currentTime = day.date.clone().startOf('day');

    for (let i = 0; i < 24; i++) {
        times.push(currentTime.clone());
        currentTime.add(60 * 60 * 1000);
    }

    return (
        <div className="rain-schedule">
            <div className="rain-schedule-header">
                <span className="rain-schedule-month">{day.date.format('MMM')}</span>
                <span className="rain-schedule-year">{day.date.format('YYYY')}</span>
                <span className="rain-schedule-controls">
                    <Button shape="circle" size="small" icon={<LeftOutlined />} onClick={onPrevClick} />
                    <Button shape="circle" size="small" icon={<RightOutlined />} onClick={onNextClick} />
                </span>
            </div>
            <div className="rain-schedule-days-header">
                {secondaryTimeZone && (
                    <span className="rain-schedule-secondary-tz">{moment.tz(secondaryTimeZone).zoneAbbr()}</span>
                )}
                <span className="rain-schedule-primary-tz">{moment.tz(primaryTimeZone).zoneAbbr()}</span>
                {days.map((day, dayIndex) => (
                    <div key={`day-${dayIndex}`} className="rain-schedule-day-header">
                        <span>{day.date.format('ddd')}</span>
                        <span>{day.date.format('DD')}</span>
                    </div>
                ))}
            </div>
            <div className="rain-schedule-days">
                <div>
                    {times.map((time, timeIndex) => {
                        return (
                            <div key={`time-${timeIndex}`} className="rain-schedule-times">
                                {secondaryTimeZone && (
                                    <span className="rain-schedule-secondary-tz">
                                        {time.tz(secondaryTimeZone).format('h A')}
                                    </span>
                                )}
                                <span className="rain-schedule-primary-tz">
                                    {time.tz(primaryTimeZone).format('h A')}
                                </span>
                            </div>
                        );
                    })}
                </div>
                {dayElements.map((dayElement, dayIndex) => (
                    <div className="rain-schedule-day" key={dayIndex}>
                        {dayElement}
                    </div>
                ))}
            </div>
        </div>
    );
};
export default ALPHA_Schedule;

/**
 * Marks slots between busy slots as busy when the number of time slots
 * between the two busy slots does not meet the minimum number of
 * slots given the select duration. Assuming busy slots are 15 minutes
 * the minumum number of slots is selectDuration / 15.
 * @param busy list of busy slots
 * @param selectDuration duration used to calculate the minimum number of slots
 */
export const markBusySlots = (busy: boolean[], selectDuration: number) => {
    if (busy.length < 3) return [...busy];
    const busySlots = busy.reduce((busySlots: number[], isBusy, index) => {
        if (isBusy) {
            busySlots.push(index);
        }
        return busySlots;
    }, []);
    const minNumberOfSlots = selectDuration / 15;
    const result = [...busy];
    for (let i = 0; i < busySlots.length - 1; i += 1) {
        if ((busySlots[i + 1] ?? 0) - (busySlots[i] ?? 0) - 1 < minNumberOfSlots) {
            for (let j = (busySlots[i] ?? 0) + 1; j < (busySlots[i + 1] ?? 0); j += 1) {
                result[j] = true;
            }
        }
    }
    return result;
};
